import type { ParsedEducation, ParsedExperience } from "@tabbio/types";
import { stripHtmlFromPlainText } from "@tabbio/utils";
import { formatEducationDegreeForSource } from "@tabbio/utils/education-language";
import { composeEducationDescription } from "@tabbio/utils/education-description";
import type { CVEducation, CVExperience, CVProfile } from "./cv-mock-data";
import { formatDateForPicker, formatPeriod, generateId } from "./cv-mapping";

export function buildAiDraftProfileContext(profile: Partial<CVProfile>) {
  return {
    name: profile.name ? stripHtmlFromPlainText(profile.name) : undefined,
    title: profile.headline
      ? stripHtmlFromPlainText(profile.headline)
      : undefined,
    skills:
      profile.skillGroups
        ?.flatMap((group) => group.items || [])
        .filter(Boolean)
        .map(stripHtmlFromPlainText)
        .slice(0, 20) || [],
  };
}

export function mapAIDraftExperienceToCVExperience(
  experience: ParsedExperience,
  fallbackPrompt: string,
): CVExperience {
  const safeFallback = stripHtmlFromPlainText(fallbackPrompt);
  return {
    id: generateId(),
    role: stripHtmlFromPlainText(experience.title || ""),
    company: stripHtmlFromPlainText(experience.company || ""),
    location: stripHtmlFromPlainText(experience.location || ""),
    locationType: experience.locationType || "",
    employmentType: stripHtmlFromPlainText(
      experience.employmentType || "Full-time",
    ),
    period: formatPeriod(
      experience.startDate,
      experience.endDate,
      experience.current,
    ),
    overview: stripHtmlFromPlainText(experience.description || safeFallback),
    bullets: Array.isArray(experience.achievements)
      ? experience.achievements.map(stripHtmlFromPlainText).filter(Boolean)
      : [],
  };
}

export function mapAIDraftEducationToCVEducation(
  education: ParsedEducation,
  fallbackPrompt: string,
): CVEducation {
  const degreeName = stripHtmlFromPlainText(education.degree?.trim() || "");
  const field = stripHtmlFromPlainText(education.fieldOfStudy?.trim() || "");
  // CVEducation has no separate field-of-study slot, so read the field into the
  // degree label the way a CV states it ("Bachelor's degree in Computer Science")
  // instead of burying it at the front of the description.
  const degree = formatEducationDegreeForSource(
    degreeName,
    field,
    fallbackPrompt,
  );
  return {
    id: generateId(),
    degree,
    school: stripHtmlFromPlainText(education.school || ""),
    year: formatDateForPicker(education.endDate || education.startDate || ""),
    description:
      composeEducationDescription({
        grade: stripHtmlFromPlainText(education.grade || ""),
        description: stripHtmlFromPlainText(education.description || ""),
        details: (education.details ?? []).map(stripHtmlFromPlainText),
      }) || stripHtmlFromPlainText(fallbackPrompt),
  };
}
